import { useEffect, useState } from "react";

import { api } from "../../api/client";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { Field } from "../../components/forms/Field";
import { ResponsiveTable } from "../../components/ui/ResponsiveTable";
import { apiError } from "../../lib/apiError";
import { formatDateTime } from "../../lib/format";

type AuditLogItem = {
  id: number;
  timestamp: string;
  user_id: number | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  ip_address: string;
  status: string;
  details: Record<string, unknown> | null;
};

function toCsv(rows: AuditLogItem[]): string {
  const header = [
    "id",
    "timestamp",
    "user_id",
    "action",
    "resource_type",
    "resource_id",
    "ip_address",
    "status",
    "details",
  ];
  const lines = rows.map((row) =>
    [
      row.id,
      row.timestamp,
      row.user_id ?? "",
      row.action,
      row.resource_type ?? "",
      row.resource_id ?? "",
      row.ip_address,
      row.status,
      row.details ? JSON.stringify(row.details) : "",
    ]
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(","),
  );
  return [header.join(","), ...lines].join("\n");
}

export function AdminAuditPage() {
  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [status, setStatus] = useState("");
  const [userId, setUserId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const pageSize = 50;

  async function load(targetPage = page) {
    setError(null);
    const result = await api.GET("/api/platform/audit-logs", {
      params: {
        query: {
          page: targetPage,
          page_size: pageSize,
          action: action || undefined,
          status: status || undefined,
          user_id: userId ? Number(userId) : undefined,
        },
      },
    });

    if (result.error) {
      setError(apiError(result.error, "Nie udało się wczytać rejestru audytowego."));
      return;
    }

    const data = result.data as {
      items: AuditLogItem[];
      total: number;
      page: number;
    };
    setItems(data.items);
    setTotal(data.total);
    setPage(data.page);
  }

  useEffect(() => {
    void load(1);
  }, []);

  function exportCsv() {
    const blob = new Blob([toCsv(items)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit-logs-page-${page}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="page__content page__content--admin">
      <AdminPageIntro>
        Zdarzenia bezpieczeństwa i operacje administracyjne.
      </AdminPageIntro>

      {error ? (
        <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      ) : null}

      <div className="toolbar admin-filters">
        <Field label="Akcja">
          <input
            className="input"
            value={action}
            onChange={(event) => setAction(event.target.value)}
            placeholder="np. login"
          />
        </Field>
        <Field label="Status">
          <select
            className="input"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Wszystkie</option>
            <option value="success">success</option>
            <option value="failure">failure</option>
          </select>
        </Field>
        <Field label="ID użytkownika">
          <input
            className="input"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
            inputMode="numeric"
          />
        </Field>
        <div className="toolbar__actions">
          <button type="button" className="btn" onClick={() => void load(1)}>
            Filtruj
          </button>
          <button type="button" className="btn btn--ghost" onClick={exportCsv}>
            Eksport CSV (strona)
          </button>
        </div>
      </div>

      <div className="tbl-wrap">
        <ResponsiveTable className="tbl">
          <thead>
            <tr>
              <th>Czas</th>
              <th>Użytkownik</th>
              <th>Akcja</th>
              <th>Status</th>
              <th>IP</th>
              <th>Szczegóły</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{formatDateTime(item.timestamp)}</td>
                <td>{item.user_id ?? "—"}</td>
                <td>{item.action}</td>
                <td>{item.status}</td>
                <td>{item.ip_address || "—"}</td>
                <td>
                  {item.details ? (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() =>
                        setExpandedId((current) => (current === item.id ? null : item.id))
                      }
                    >
                      {expandedId === item.id ? "Ukryj" : "Pokaż"}
                    </button>
                  ) : (
                    "—"
                  )}
                  {expandedId === item.id && item.details ? (
                    <pre className="admin-details">{JSON.stringify(item.details, null, 2)}</pre>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      </div>

      <div className="pagination">
        <button
          type="button"
          className="btn btn--ghost"
          disabled={page <= 1}
          onClick={() => void load(page - 1)}
        >
          Poprzednia
        </button>
        <span className="muted">
          Strona {page} / {totalPages} ({total} wpisów)
        </span>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={page >= totalPages}
          onClick={() => void load(page + 1)}
        >
          Następna
        </button>
      </div>
    </div>
  );
}
